import { Duration } from "luxon";
import { useEffect, useRef } from "react";

import { userOfficeLeave } from "../../services/api.service.js";
import { useLayoutStore } from "../../store/layout.store.js";
import { useAuth } from "../auth/use-auth.js";
import { useOffice } from "./use-office.js";

const LEAVE_REQUEST_GRACE_MS = Duration.fromObject({ seconds: 1.5 }).toMillis();

export function useOfficeExit() {
  const { accessToken } = useAuth();
  const { team } = useOffice();
  const requestRef = useRef<Promise<void> | null>(null);
  const accessTokenRef = useRef(accessToken);

  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  const leaveOffice = ({
    keepalive = false,
  }: {
    keepalive?: boolean;
  } = {}) => {
    useLayoutStore.getState().setIsLeavingOffice(true);

    if (requestRef.current) {
      return requestRef.current;
    }

    if (!accessTokenRef.current) {
      return Promise.resolve();
    }

    const abortController = keepalive ? null : new AbortController();
    const timeoutId = abortController
      ? setTimeout(() => abortController.abort(), LEAVE_REQUEST_GRACE_MS)
      : undefined;
    const request = async () => {
      try {
        await userOfficeLeave(
          { msTeamId: team.msTeamId },
          {
            keepalive,
            ...(abortController ? { signal: abortController.signal } : {}),
          }
        );
      } finally {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      }
    };

    requestRef.current = request();

    return requestRef.current;
  };

  useEffect(() => {
    const leaveOnLifecycleEvent = async () => {
      try {
        await leaveOffice({ keepalive: true });
      } catch {
        // Browser lifecycle cleanup is best effort; the server lease is the fallback.
      }
    };
    const requestLifecycleCleanup = () => {
      void leaveOnLifecycleEvent();
    };
    window.addEventListener("beforeunload", requestLifecycleCleanup);
    window.addEventListener("pagehide", requestLifecycleCleanup);

    return () => {
      window.removeEventListener("beforeunload", requestLifecycleCleanup);
      window.removeEventListener("pagehide", requestLifecycleCleanup);
    };
  });

  return { leaveOffice };
}
